/**
 * PullRequestPane
 *
 * The third tool in the work pane: the pull requests open on this workspace,
 * read out of the bare repository beside it. There is no code host here and
 * there is no network — `origin.git` *is* the server, and a pull request is a
 * file in it (docs/github.md) — so this is a reader of files, exactly as the
 * git graph is a reader of refs.
 *
 * The rule that shapes this file: **it reads, and nothing here writes.** Not a
 * button that merges, closes, approves or replies. Every one of those is a
 * command the learner types in the terminal three inches below, which is the
 * whole point of the level; a button would be the product doing the exercise.
 * What the pane is for is the other half — being able to look at the thing you
 * are answering while you answer it.
 *
 * It reads on the same signal the graph does, a command having finished, and
 * only while it is the visible tab. A hidden reader would run a second script
 * in the guest after every prompt for a panel nobody is looking at.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, type PullRequest } from '../../api';
import { fullDateTime, ltr } from '../../lib/format';
import { Machine, Parts, Tag, type TagTone } from '../../ui';
import { useStrings } from '../../state/useLocale';
import type { Strings } from '../../lib/strings';

export interface PullRequestPaneProps {
  sessionId: string;
  /**
   * Whether this is the tab on show. False does not unmount anything — nothing
   * in the work pane is ever unmounted — it only stops the reading.
   */
  active: boolean;
  /**
   * Any change means "a command finished, look again". The number itself means
   * nothing; only that it is not the one already read.
   */
  refreshKey: number;
  /** How many were found, or null while that is not yet known. Drawn in the pane head. */
  onCount?(count: number | null): void;
}

type Pulls = Strings['play']['pulls'];

/* ── The words for a machine field ────────────────────────────────────────── */

/**
 * A state the JSON does not use is drawn as itself.
 *
 * The three below are the contract, but the file is editable and a learner who
 * writes one by hand has done the work (docs/github.md §1). Showing `reopened`
 * as `reopened` is the honest answer; showing nothing is not.
 */
function stateLabel(s: Pulls, state: string): string {
  if (state === 'open') return s.state.open;
  if (state === 'merged') return s.state.merged;
  if (state === 'closed') return s.state.closed;
  return state;
}

/** Open is the one that is still somebody's problem, so it is the bright one. */
function stateTone(state: string): TagTone {
  if (state === 'open') return 'on';
  if (state === 'closed') return 'off';
  return 'plain';
}

function checkLabel(s: Pulls, status: string): string {
  if (status === 'pass') return s.checkPass;
  if (status === 'fail') return s.checkFail;
  if (status === 'pending') return s.checkPending;
  return status;
}

function reviewLabel(s: Pulls, state: string): string {
  if (state === 'approved') return s.reviewApproved;
  if (state === 'changes_requested') return s.reviewChanges;
  if (state === 'commented') return s.reviewCommented;
  return state;
}

/** An ISO timestamp from the file, or nothing at all if it is not one. */
function when(iso: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : fullDateTime(date);
}

/* ── One pull request ─────────────────────────────────────────────────────── */

function PullRequestCard({ pull, s }: { pull: PullRequest; s: Pulls }) {
  const id = `pull-${pull.number}`;
  const date = when(pull.created);

  return (
    <article className="pull" aria-labelledby={`${id}-title`}>
      <header className="pull-head">
        <h3 className="pull-title" id={`${id}-title`}>
          {/* A number and a title, both written inside the repository. The
              repository is not translated (docs/i18n.md §6.1), so both keep
              the direction they were written in. */}
          <span className="pull-number" dir="ltr">
            #{pull.number}
          </span>
          <span className="pull-subject" dir="ltr">
            {pull.title}
          </span>
        </h3>
        <span className="pull-tags">
          {pull.draft && <Tag tone="off">{s.draft}</Tag>}
          <Tag tone={stateTone(pull.state)}>{stateLabel(s, pull.state)}</Tag>
        </span>
      </header>

      {/* Two branch names and an arrow: not a sentence, and read as one it is
          nonsense, so the sentence is said separately and the arrow is drawn
          for the eye. Left to right in both languages — the arrow points from
          the branch into the branch it lands in, and mirroring it would point
          the merge the wrong way. */}
      <p className="pull-branches">
        <span className="sr-only">{s.mergesInto(ltr(pull.head), ltr(pull.base))}</span>
        <span className="pull-refs" dir="ltr" aria-hidden="true">
          <code>{pull.base}</code>
          <span className="pull-arrow">←</span>
          <code>{pull.head}</code>
        </span>
      </p>

      {(pull.author || date) && (
        <p className="pull-meta note">
          {pull.author ? (
            <Parts of={s.openedBy(<Machine key="author">{pull.author}</Machine>, date)} />
          ) : (
            date
          )}
        </p>
      )}

      {pull.body && (
        <div className="pull-body" dir="ltr">
          {pull.body}
        </div>
      )}

      {pull.checks.length > 0 && (
        <section className="pull-block">
          <h4 className="label">{s.checks}</h4>
          <ul className="pull-list-plain pull-checks">
            {pull.checks.map((check, i) => (
              <li key={`${check.name}-${i}`} className={`pull-check is-${check.status}`}>
                <span className="pull-check-mark" aria-hidden="true" />
                <code className="pull-check-name">{check.name}</code>
                <span className="pull-check-state">{checkLabel(s, check.status)}</span>
                {check.detail && (
                  <span className="pull-check-detail" dir="ltr">
                    {check.detail}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {pull.reviews.length > 0 && (
        <section className="pull-block">
          <h4 className="label">{s.reviews}</h4>
          <ul className="pull-list-plain pull-reviews">
            {pull.reviews.map((review, i) => (
              <li key={`${review.author}-${i}`} className="pull-review">
                <div className="pull-review-head">
                  <Machine className="pull-who">{review.author}</Machine>
                  <span className="pull-review-state">{reviewLabel(s, review.state)}</span>
                </div>
                {review.body && (
                  <div className="pull-said" dir="ltr">
                    {review.body}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {pull.commits.length > 0 && (
        <section className="pull-block">
          <h4 className="label">{s.commits}</h4>
          {/* A commit row is `git log` output, whole: hash and subject are both
              the machine's, so the list keeps the machine's direction. */}
          <ol className="pull-list-plain pull-commits" dir="ltr">
            {pull.commits.map((commit) => (
              <li key={commit.hash}>
                <code className="pull-hash">{commit.short}</code>
                <span className="pull-commit-subject">{commit.subject}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {pull.files.length > 0 && (
        <section className="pull-block">
          <h4 className="label">{s.files}</h4>
          <ul className="pull-list-plain pull-files" dir="ltr">
            {pull.files.map((file) => (
              <li key={`${file.status}-${file.path}`}>
                {/* git's own letter — A, M, D, R100 — left as git wrote it. */}
                <code className="pull-file-status">{file.status}</code>
                <code className="pull-path">{file.path}</code>
              </li>
            ))}
          </ul>
        </section>
      )}

      {pull.comments.length > 0 && (
        <section className="pull-block">
          <h4 className="label">{s.comments}</h4>
          <ul className="pull-list-plain pull-comments">
            {pull.comments.map((comment, i) => (
              <li key={comment.id || i} className={comment.resolved ? 'pull-comment is-resolved' : 'pull-comment'}>
                <div className="pull-comment-head">
                  <Machine className="pull-who">{comment.author}</Machine>
                  {/* Where it is anchored. A path and a line number are the
                      one thing in a review you have to be able to read
                      character for character, so they stay left to right. */}
                  {comment.path ? (
                    <code className="pull-anchor" dir="ltr">
                      {comment.line === null ? comment.path : `${comment.path}:${comment.line}`}
                    </code>
                  ) : (
                    <span className="pull-anchor-none">{s.onTheBranch}</span>
                  )}
                  {comment.resolved && <span className="pull-resolved">{s.resolved}</span>}
                </div>
                <div className="pull-said" dir="ltr">
                  {comment.body}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

/* ── The pane ─────────────────────────────────────────────────────────────── */

export function PullRequestPane({ sessionId, active, refreshKey, onCount }: PullRequestPaneProps) {
  const s = useStrings().play.pulls;
  const [pulls, setPulls] = useState<PullRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // What was last read, so that becoming visible again does not re-read what
  // has not changed, and so a new session starts from nothing rather than from
  // the last level's pull requests.
  const read = useRef<{ id: string; key: number } | null>(null);

  useEffect(() => {
    read.current = null;
    setPulls(null);
    setError(null);
  }, [sessionId]);

  useEffect(() => {
    if (!active) return;
    const at = read.current;
    if (at && at.id === sessionId && at.key === refreshKey) return;
    read.current = { id: sessionId, key: refreshKey };
    let cancelled = false;
    api.pullRequests(sessionId).then(
      (list) => {
        if (cancelled) return;
        setPulls(list);
        setError(null);
      },
      (err: unknown) => {
        if (cancelled) return;
        // Let the next command try again rather than sitting on a failure.
        read.current = null;
        setError(err instanceof Error ? err.message : s.readFailed);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [sessionId, active, refreshKey, s]);

  // Held in a ref and fired only on a real change, so a host that passes an
  // inline callback and stores the result in state cannot loop.
  const countRef = useRef(onCount);
  useEffect(() => {
    countRef.current = onCount;
  });
  useEffect(() => {
    countRef.current?.(error !== null ? null : pulls === null ? null : pulls.length);
  }, [pulls, error]);

  const body = (): ReactNode => {
    if (error) {
      // The message came off the engine in English, so it is isolated rather
      // than run together with the Arabic around it.
      return (
        <p className="pull-empty note">
          <Parts of={s.unavailable(<Machine key="err">{error}</Machine>)} />
        </p>
      );
    }
    if (pulls === null) return <p className="pull-empty note">{s.reading}</p>;
    if (pulls.length === 0) {
      // Not an error and not a failure of anything: most levels never open a
      // pull request, and the pane says what would put one here.
      return (
        <div className="pull-blank">
          <span className="label">{s.emptyTitle}</span>
          <p className="note">{s.emptyNote}</p>
        </div>
      );
    }
    return (
      <ul className="pull-list" aria-label={s.label}>
        {pulls.map((pull) => (
          <li key={pull.number}>
            <PullRequestCard pull={pull} s={s} />
          </li>
        ))}
      </ul>
    );
  };

  return <div className="pulls">{body()}</div>;
}

export default PullRequestPane;
